import React, { JSX } from "react"
import { useTranslation } from "react-i18next";
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';
import { Box, Button, DialogActions, DialogContent, DialogContentText, ListItemButton, Typography } from "@mui/material";
import Checkbox from '@mui/material/Checkbox';

import { Features, isEnabled } from "../utils/featureToggle";
import environment from "../utils/environment";
import { useCacheBuster } from "react-cache-buster";
import { version } from '../../package.json';

export interface AdvancedSettingsProps {
  open: boolean;
  onClose: () => void;
} 

type listItem<T> = {
  key: T;
  name: string;
  active: boolean;
  originalValue?: boolean;
}

export const AdvancedSettings = ({ onClose }: AdvancedSettingsProps): JSX.Element  => {
  const { i18n } = useTranslation();

  const { checkCacheStatus } = useCacheBuster();
  
  const [showDialog, setShowDialog] = React.useState<boolean>(true);
  const [features, setFeatures] = React.useState<listItem<Features>[]>([]);

  const handleClose = () => {
    setShowDialog(false);
    onClose();
  };

  // Create and load list of active features on mount
  React.useEffect(() => {
    if (showDialog) {
      const featureList = Object.entries(Features).map(([key, value]) => ({
        key: value,
        name: key,
        active: isEnabled(value),
        originalValue: environment.FEATURES?.includes(value),
      }));
      setFeatures(featureList);
    }
  }, [showDialog]);

  
  // Handler to activate/deactivate features and roles.
  const handleToggleChange = function(
    key: string,
    newState: boolean,
  ) {
    const list = [...features];
    const index = list.findIndex(item => item.key === key);
    list[index].active = newState;
    setFeatures(list as listItem<Features>[]);
  };
  
    // Save changes handler when save button is clicked.
  const save = function() {
    // handle feature changes
    features.forEach(item => {
      if (item.active !== item.originalValue) {
        if (item.active) {
          localStorage.setItem(item.key, 'true');
        } else {
          localStorage.setItem(item.key, 'false');
        }
      } else {
        localStorage.removeItem(item.key);
      }
    });

    setShowDialog(false);
    window.location.reload();
  };


  return (
    <Dialog maxWidth="md" onClose={handleClose} open={showDialog}>
      <Grid item xs={6}></Grid>
      <DialogTitle>Advanced settings</DialogTitle>
      <DialogContent>
        <DialogContentText>Select features to activated/deactive below</DialogContentText>
        <List sx={{ width: "100%" }}>
          {features
          .sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          })
          .map((value) => {
            const labelId = `checkbox-list-label-${value}`;
            
            return (
              <ListItem key={value.name} disablePadding>
                <ListItemButton role={undefined} onClick={() => handleToggleChange(value.name, !value.active)} dense>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={value.active}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': labelId }}
                    />  
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.name} />
                </ListItemButton>    
              </ListItem>
            )
          })}
        </List>
        <DialogContentText>Version info</DialogContentText>
        <Box marginTop={2} >
          <Typography>Version: <strong>{ version }</strong></Typography>
          <Button onClick={checkCacheStatus}>Check for new version</Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={() => setShowDialog(false)}>
          Cancel
        </Button>
        <Button onClick={save} autoFocus>
          Save &amp; Reload
        </Button>
      </DialogActions>

    </Dialog>

  );
}